<template>
    <ModeSwitcher
        ref="modeSwitcher"
        :main-mode-text="isTO1 && (orderId || preset) ? $t('create') : null"
        :create-mode-text="!preset ? $t('create_internal_note') : $t('create_preset_note')"
        :edit-mode-text="!preset ? $t('edit_internal_note') : $t('edit_preset_note')"
        :is-hide-back-button="isHideBackButton"
        @reset="resetData"
    >
        <template v-slot:additional-mode>
            <internal-note-edit-form
                ref="form"
                v-model="communicationData"
                :services="orderServices"
                :specified-service="specifiedService"
                :_valid="valid"
                :edit-note-info="editNoteInfo"
                :preset="preset"
                @change-valid="valid = $event"
            />
            <div class="d-flex align-center mt-8">
                <v-btn color="primary" :loading="loading" :disabled="!valid" @click="save">
                    {{ $t('save') }}
                </v-btn>
                <v-btn v-if="!isHideBackButton" variant="text" @click="goToTable">
                    {{ $t('cancel') }}
                </v-btn>
            </div>
        </template>
        <template v-slot:main-mode>
            <slot :get-communications="getCommunications" />
            <v-data-table
                :headers="headers"
                :items="communicationsFiltered"
                :loading="loading || communicationsLoading"
                class="elevation-1"
                :items-length="
                    communicationsTotalCount || (communicationsFiltered && communicationsFiltered.length) || 0
                "
                :items-per-page="tableOptions.itemsPerPage"
                :footer-props="{'items-per-page-options': [5, 15, 30, -1]}"
                :hide-default-header="isMobile"
                :mobile-breakpoint="0"
                disable-sort
                @update:options="onChangeTableOptions"
            >
                <template v-slot:item.description="{item}">
                    <div v-html="item.description" />
                </template>
                <template v-slot:item.status="{item}">
                    <v-switch
                        :model-value="item.status === 'ACTIVE'"
                        :label="$t('active')"
                        color="primary"
                        class="my-0"
                        hide-details
                        @click="switchCommunicationActive(item)"
                    />
                </template>
                <template v-slot:item.edit="{item}">
                    <v-btn
                        color="primary"
                        size="small"
                        icon="mdi-pencil"
                        variant="text"
                        @click="editCommunication(item)"
                    />
                </template>
                <template v-slot:item.DR="{item}">
                    <div>
                        {{
                            `${item.service ? item.service.serviceName : item.serviceName || ''}${
                                item.service.roomTypeName ? `, ${item.service.roomTypeName}` : ''
                            }`
                        }}
                    </div>
                </template>
                <template v-slot:item.communicationDateTime="{item}">
                    {{ $filters.dateTimeFormat(item.communicationDateTime) }}
                </template>
                <template v-slot:item.assignedUser="{item}">
                    {{ item.assignedUser ? userName(item.assignedUser) : '' }}
                </template>
                <template v-slot:item.mobile="{item}">
                    <div class="py-2 d-flex full-width">
                        <div class="d-flex flex-column flex-grow-1 flex-shrink-1 overflow-hidden">
                            <div>{{ $filters.dateTimeFormat(item.communicationDateTime) }}</div>
                            <div v-html="item.description" />
                        </div>
                        <div class="pl-2 d-flex flex-column flex-grow-0 flex-shrink-0 align-end overflow-hidden">
                            <div>{{ item.assignedUser ? userName(item.assignedUser) : '' }}</div>
                            <div>{{ item.status === 'ACTIVE' ? $t('active') : $t('inactive') }}</div>
                        </div>
                    </div>
                </template>
                <template v-slot:footer.prepend>
                    <span v-if="communicationsTotalCount" class="text-caption text--secondary">
                        {{ $t('total') }}: {{ communicationsTotalCount }}
                    </span>
                </template>
            </v-data-table>
        </template>
    </ModeSwitcher>
</template>
<script lang="ts">
    import {Component, Prop} from 'vue-facing-decorator'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import TimeField from '@/components/snippets/forms/TimeField.vue'
    import InternalNoteEditForm from '@/components/account/internalNotes/InternalNoteEditForm.vue'
    import Communications, {
        Communications as _Communications,
    } from '@/components/account/communications/Communications.vue'

    const blankCommunication = (communicationDateTime, orderId = null) => ({
        type: 'NOTES',
        communicationDateTime,
        description: '',
        active: true,
        showInInvoice: false,
        showInQuotation: false,
        processId: null,
        visibleForClients: false,
        visibleForSuppliers: false,
        orderId,
        subject: null,
    })

    @Component({
        components: {InternalNoteEditForm, TimeField, DatePicker, Communications},
    })
    export class InternalNotes extends _Communications {
        @Prop() specifiedService
        @Prop({default: false, type: Boolean}) preset

        valid = false
        communicationData = blankCommunication()
        editNoteInfo = null
        active = true
        orderServices = []

        mounted() {
            this.orderServices = this.services || []
        }

        resetData() {
            this.communicationData = blankCommunication(this.$dateFns.format(new Date(), "yyyy-MM-dd'T'HH:mm"))
        }

        async editCommunication(e) {
            await this.setCommunicationData(e)

            this.editNoteInfo = {
                created: e.createdDateTime,
                modified: e.modifiedDateTime,
                createdUser: e.createdByUserName,
                modifiedUser: e.modifiedByUserName,
            }

            this.$refs.modeSwitcher.edit()
            this.goTo(0)
        }

        async setCommunicationData(e) {
            if (!this.services && e.reservationNumber) {
                try {
                    this.orderServices = (
                        await this.$api.orders.get({
                            orderType: authStore.orderType,
                            orderId: +e.reservationNumber,
                            responseType: 'SHORT',
                        })
                    ).orders[0].services
                } catch (e) {
                    this.orderServices = []
                }
            } else {
                this.orderServices = this.services
            }
            const processId =
                this.specifiedService?.processId ||
                this.orderServices?.find(item => item.serviceName === e.serviceName)?.processId

            this.communicationData = {
                assignedToId: e.assignedUser.id,
                accountId: e.accountId,
                accountType: e.accountType,
                communicationDateTime: e.communicationDateTime?.split(' ').join('T'),
                description: e.description,
                orderId: e.reservationNumber,
                processId,
                active: e.status === 'ACTIVE',
                showInInvoice: e.showInInvoice,
                showInQuotation: e.showInQuotation,
                visibleForClients: e.visibleForClients,
                visibleForSuppliers: e.visibleForSuppliers,
                communicationId: e.communicationId,
                subject: e.subject,
            }
        }

        get communicationsFiltered() {
            return this.communications
                .filter(({serviceId}) => !this.specifiedService || this.specifiedService.serviceId === serviceId)
                .filter(e => (this.active ? e.status === 'ACTIVE' : e.status === 'INACTIVE'))
                .map(el => ({
                    ...el,
                    ...(this.services?.find(s => s.serviceId === el.serviceId) && {
                        service: this.services?.find(s => s.serviceId === el.serviceId),
                    }),
                    visibleNote: [
                        this.$t('for_to1'),
                        ...(el.visibleForClients ? [this.$t('for_clients')] : []),
                        ...(el.visibleForSuppliers ? [this.$t('for_suppliers')] : []),
                    ].join(', '),
                }))
        }

        get requestData() {
            const {
                type,
                communicationDateTime,
                description,
                processId,
                active,
                showInInvoice,
                showInQuotation,
                visibleForClients,
                visibleForSuppliers,
                communicationId,
                subject,
                orderId,
            } = this.communicationData
            return {
                assignedToId: this.person.personId,
                accountId: this.accountId,
                accountType: this.accountType,
                type,
                communicationDateTime,
                description,
                orderId: this.orderId || orderId,
                processId,
                active,
                showInInvoice,
                showInQuotation,
                visibleForClients,
                visibleForSuppliers,
                ...(this.communicationData.communicationId && {communicationId}),
                preset: this.preset,
                subject,
            }
        }

        get headers() {
            if (this.isMobile) return [{value: 'mobile'}]

            return [
                {title: `${this.$t('date')}/${this.$t('time')}`, value: 'communicationDateTime', minWidth: '120px'},
                {title: this.$t('title'), value: 'subject', minWidth: '150px'},
                {title: this.$t('internal_note', 1), value: 'description', minWidth: '180px'},
                ...(!this.preset ? [{title: this.$t('order_number'), value: 'reservationNumber'}] : []),
                ...(!this.specifiedService && !this.preset
                    ? [{title: this.$t('relates_to'), value: 'serviceName'}]
                    : []),
                {title: this.$t('visible_for'), value: 'visibleNote', minWidth: '140px'},
                ...(!this.specifiedService ? [{title: this.$t('user'), value: 'assignedUser'}] : []),
                ...(this.isTO1 || this.isAgency
                    ? [{title: this.$t('status'), value: 'status', width: '140px', minWidth: '140px'}]
                    : []),
                ...(this.isTO1 ? [{title: this.$t('edit'), value: 'edit'}] : []),
            ]
        }

        get accountType() {
            if (this.isCorporate) {
                return 'CORPORATE_CLIENT'
            }
            if (this.isTO1 || this.isTO2 || this.isAgency) {
                return 'AGENCY_DISTRIBUTOR'
            }
            if (this.isSupplier) {
                return 'SUPPLIER'
            }

            return 'PRIVATE_CLIENT'
        }

        get accountId() {
            if (this.isB2B) {
                return this.company.id
            }
            return this.person.personId
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get isB2B() {
            return authStore.isB2B
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isCorporate() {
            return authStore.isAgency
        }

        get isSupplier() {
            return authStore.isSupplier
        }

        get person() {
            return authStore.person
        }

        get company() {
            return authStore.company
        }
    }

    export default InternalNotes
</script>
